/**
 * NowClipboard v1.1.9
 * Modern clipboard utility library - Clipboard API + execCommand fallback + Node.js adapter
 * Zero dependencies, supports both browser and Node.js environments
 * 
 * Licensed MIT © Spicy-Mustard
 */
(function (root, factory) {
  if (typeof module === 'object' && typeof module.exports === 'object') {
    module.exports = factory();
  } else if (typeof define === 'function' && define.amd) {
    define([], factory);
  } else {
    root.NowClipboard = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ========================================
  // 1. Utility Functions & Environment Detection
  // ========================================

  var _isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  var _isNode = typeof process !== 'undefined' && process.versions != null && process.versions.node != null;

  function _isString(val) {
    return typeof val === 'string' || val instanceof String;
  }

  function _isFunction(val) {
    return typeof val === 'function';
  }

  function _isElement(val) {
    return val != null && typeof val === 'object' && val.nodeType === 1;
  }

  function _isNodeList(val) {
    var s = Object.prototype.toString.call(val);
    return val != null && (s === '[object NodeList]' || s === '[object HTMLCollection]') &&
      'length' in val && (val.length === 0 || _isElement(val[0]));
  }

  /**
   * Detect iOS Safari (for clipboard workaround)
   */
  var _isIOS = _isBrowser && /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;

  /**
   * Check if modern Clipboard API is available
   */
  function isClipboardAPIAvailable() {
    return _isBrowser &&
      navigator.clipboard != null &&
      typeof navigator.clipboard.writeText === 'function';
  }

  /**
   * Check if ClipboardItem API is available (for image/rich text copy)
   */
  function isClipboardItemSupported() {
    return _isBrowser &&
      navigator.clipboard != null &&
      typeof navigator.clipboard.write === 'function' &&
      typeof ClipboardItem === 'function';
  }

  /**
   * Check if clipboard.read() is available (for rich read)
   */
  function isClipboardReadSupported() {
    return _isBrowser &&
      navigator.clipboard != null &&
      typeof navigator.clipboard.read === 'function';
  }

  /**
   * Check if execCommand supports the specified action
   */
  function isExecCommandSupported(action) {
    if (!_isBrowser) return false;
    try {
      return !!document.queryCommandSupported && document.queryCommandSupported(action);
    } catch (e) {
      return false;
    }
  }

  // ========================================
  // 2. EventEmitter
  // ========================================

  function EventEmitter() {
    this._events = {};
  }

  EventEmitter.prototype.on = function (event, fn, ctx) {
    if (!_isString(event) || !_isFunction(fn)) return this;
    var events = this._events[event] || (this._events[event] = []);
    events.push({ fn: fn, ctx: ctx, once: false });
    return this;
  };

  EventEmitter.prototype.once = function (event, fn, ctx) {
    if (!_isString(event) || !_isFunction(fn)) return this;
    var events = this._events[event] || (this._events[event] = []);
    events.push({ fn: fn, ctx: ctx, once: true });
    return this;
  };

  EventEmitter.prototype.off = function (event, fn) {
    if (!_isString(event)) return this;
    var events = this._events[event];
    if (!events) return this;
    if (!fn) {
      delete this._events[event];
      return this;
    }
    var remaining = [];
    for (var i = 0; i < events.length; i++) {
      if (events[i].fn !== fn) {
        remaining.push(events[i]);
      }
    }
    if (remaining.length) {
      this._events[event] = remaining;
    } else {
      delete this._events[event];
    }
    return this;
  };

  EventEmitter.prototype.emit = function (event) {
    var events = this._events[event];
    if (!events || events.length === 0) return this;
    var args = Array.prototype.slice.call(arguments, 1);
    // Clone to prevent modification during callbacks
    var listeners = events.slice();
    var toRemove = [];
    for (var j = 0; j < listeners.length; j++) {
      listeners[j].fn.apply(listeners[j].ctx, args);
      if (listeners[j].once) {
        toRemove.push(listeners[j]);
      }
    }
    // Remove one-time listeners
    if (toRemove.length) {
      var remaining = [];
      for (var k = 0; k < events.length; k++) {
        if (toRemove.indexOf(events[k]) === -1) {
          remaining.push(events[k]);
        }
      }
      if (remaining.length) {
        this._events[event] = remaining;
      } else {
        delete this._events[event];
      }
    }
    return this;
  };

  EventEmitter.prototype.removeAllListeners = function () {
    this._events = {};
    return this;
  };

  // ========================================
  // 3. Selection Engine - Text Selection
  // ========================================

  /**
   * Select text in element and return its content
   * Note: Does NOT modify the element's readonly attribute to avoid MutationObserver side effects
   */
  function selectText(element) {
    var text = '';
    var nodeName = element.nodeName;

    if (nodeName === 'SELECT') {
      element.focus();
      text = element.value;
    } else if (nodeName === 'INPUT' || nodeName === 'TEXTAREA') {
      element.focus();
      try {
        element.setSelectionRange(0, element.value.length);
      } catch (e) {
        // Some input types (e.g. number, email) don't support setSelectionRange
        try { element.select(); } catch (e2) { /* ignore */ }
      }
      text = element.value;
    } else {
      if (element.hasAttribute('contenteditable')) {
        element.focus();
      }
      var selection = window.getSelection();
      var range = document.createRange();
      range.selectNodeContents(element);
      selection.removeAllRanges();
      selection.addRange(range);
      text = selection.toString();
    }

    return text;
  }

  /**
   * Create offscreen hidden textarea for fallback copy
   * Uses iOS-compatible positioning to ensure execCommand works on mobile Safari
   */
  function createOffscreenArea(text) {
    var el = document.createElement('textarea');
    el.style.fontSize = '16px'; // Prevent iOS auto-zoom on inputs < 16px
    el.style.border = '0';
    el.style.padding = '0';
    el.style.margin = '0';

    if (_isIOS) {
      // iOS Safari requires elements to be in the visible viewport for execCommand('copy')
      el.style.position = 'fixed';
      el.style.left = '0';
      el.style.top = '0';
      el.style.width = '1px';
      el.style.height = '1px';
      el.style.opacity = '0.01';
      el.style.zIndex = '99999';
    } else {
      el.style.position = 'absolute';
      el.style.opacity = '0';
      var isRTL = document.documentElement.getAttribute('dir') === 'rtl';
      el.style[isRTL ? 'right' : 'left'] = '-9999px';
      var yPos = window.pageYOffset || document.documentElement.scrollTop;
      el.style.top = yPos + 'px';
    }

    el.setAttribute('readonly', '');
    el.value = text;
    return el;
  }

  /**
   * Clear current page selection
   */
  function clearSelection() {
    try {
      if (window.getSelection) {
        var sel = window.getSelection();
        if (sel.removeAllRanges) {
          sel.removeAllRanges();
        } else if (sel.empty) {
          sel.empty();
        }
      }
    } catch (e) {
      // ignore
    }
  }

  // ========================================
  // 4. Clipboard Operations Core
  // ========================================

  /**
   * Copy text using modern Clipboard API
   */
  function modernCopy(text) {
    return navigator.clipboard.writeText(text).then(function () {
      return text;
    });
  }

  /**
   * Copy text using execCommand fallback
   */
  function legacyCopy(text, container) {
    var fakeEl = createOffscreenArea(text);
    var containerEl = container || document.body;
    containerEl.appendChild(fakeEl);

    selectText(fakeEl);

    var succeeded = false;
    try {
      succeeded = document.execCommand('copy');
    } catch (e) {
      succeeded = false;
    }

    if (fakeEl.parentNode) {
      fakeEl.parentNode.removeChild(fakeEl);
    }
    clearSelection();

    if (succeeded) {
      return resolvedPromise(text);
    }
    return rejectedPromise(new Error('execCommand copy failed'));
  }

  /**
   * Copy text from element using execCommand fallback
   */
  function legacyCopyFromElement(element, container) {
    var text = selectText(element);
    var succeeded = false;
    try {
      succeeded = document.execCommand('copy');
    } catch (e) {
      succeeded = false;
    }

    if (succeeded) {
      return resolvedPromise(text);
    }
    // Fallback: copy via temporary element
    return legacyCopy(text, container);
  }

  /**
   * Cut content from element
   * @param {Element} element - Target element
   * @param {Object} [options] - Retry options (retries/retryDelay/timeout/signal)
   */
  function performCut(element, options) {
    var text = selectText(element);
    var succeeded = false;
    try {
      succeeded = document.execCommand('cut');
    } catch (e) {
      succeeded = false;
    }

    if (succeeded) {
      return resolvedPromise(text);
    }

    // Fallback: copy first, then clear manually (with retry support)
    return copyText(text, options).then(function (copiedText) {
      // Clear editable element content
      var nodeName = element.nodeName;
      if (nodeName === 'INPUT' || nodeName === 'TEXTAREA') {
        element.value = '';
      } else if (element.hasAttribute('contenteditable')) {
        element.innerHTML = '';
      }
      return copiedText;
    });
  }

  /**
   * Unified copy entry: auto-selects best method with retry support
   */
  function copyText(text, options) {
    var opts = options || {};
    var container = opts.container || (_isBrowser ? document.body : null);

    return retryOperation(function () {
      // Prefer modern API
      if (isClipboardAPIAvailable()) {
        return modernCopy(text).catch(function () {
          // Modern API failed, fallback to execCommand
          if (_isBrowser) {
            return legacyCopy(text, container);
          }
          return rejectedPromise(new Error('Clipboard API failed and no fallback available'));
        });
      }

      // Fallback
      if (_isBrowser) {
        return legacyCopy(text, container);
      }

      // Node.js environment
      if (_isNode) {
        return nodeClipboardCopy(text);
      }

      return rejectedPromise(new Error('No clipboard method available in this environment'));
    }, opts);
  }

  /**
   * Copy text from element
   */
  function copyFromElement(element, options) {
    var opts = options || {};
    var container = opts.container || document.body;

    return retryOperation(function () {
      // Get text first
      var text = selectText(element);

      if (isClipboardAPIAvailable()) {
        return modernCopy(text).catch(function () {
          clearSelection();
          return legacyCopyFromElement(element, container);
        });
      }

      return legacyCopyFromElement(element, container);
    }, opts);
  }

  /**
   * Read clipboard text using modern Clipboard API
   * @returns {Promise<string>}
   */
  function modernRead() {
    return navigator.clipboard.readText();
  }

  /**
   * Unified read entry: auto-selects best method with retry support
   * @param {Object} [options] - Options (retries/retryDelay/timeout/signal)
   * @returns {Promise<string>}
   */
  function readText(options) {
    var opts = options || {};

    return retryOperation(function () {
      // Browser: use modern Clipboard API
      if (_isBrowser &&
          navigator.clipboard != null &&
          typeof navigator.clipboard.readText === 'function') {
        return modernRead();
      }

      // Node.js environment
      if (_isNode) {
        return nodeClipboardRead();
      }

      return rejectedPromise(new Error('Clipboard read not supported in this environment'));
    }, opts);
  }

  // ========================================
  // 4.1 Image/Blob Copy
  // ========================================

  /**
   * Convert various image sources to Blob
   * @param {Blob|File|HTMLImageElement|HTMLCanvasElement|string} source
   * @returns {Promise<Blob>}
   */
  function fetchImageBlob(source) {
    // Blob or File: return directly
    if (source instanceof Blob) {
      return resolvedPromise(source);
    }

    // HTMLCanvasElement
    if (typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement) {
      return new Promise(function (resolve, reject) {
        try {
          source.toBlob(function (blob) {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas toBlob returned null'));
            }
          }, 'image/png');
        } catch (e) {
          reject(new Error('Canvas toBlob failed: ' + e.message));
        }
      });
    }

    // HTMLImageElement
    if (typeof HTMLImageElement !== 'undefined' && source instanceof HTMLImageElement) {
      var src = source.src;
      if (!src) {
        return rejectedPromise(new Error('Image element has no src'));
      }
      return _fetchAsBlob(src);
    }

    // URL string (including data: URLs)
    if (_isString(source)) {
      return _fetchAsBlob(source);
    }

    return rejectedPromise(new TypeError('Unsupported image source type'));
  }

  /**
   * Internal helper: fetch a URL as Blob
   * Handles data: URLs efficiently without full fetch overhead
   * @param {string} url
   * @returns {Promise<Blob>}
   */
  function _fetchAsBlob(url) {
    // Fast path for data: URLs - convert directly without fetch overhead
    if (url.indexOf('data:') === 0) {
      try {
        var parts = url.split(',');
        var mimeMatch = parts[0].match(/data:([^;]+)/);
        var mime = mimeMatch ? mimeMatch[1] : 'image/png';
        var isBase64 = parts[0].indexOf('base64') !== -1;
        var data = parts.slice(1).join(',');
        var bytes;
        if (isBase64) {
          var binary = atob(data);
          bytes = new Uint8Array(binary.length);
          for (var i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
        } else {
          // Use TextEncoder for proper UTF-8 encoding (handles BMP+ characters like emoji)
          bytes = new Uint8Array(new TextEncoder().encode(decodeURIComponent(data)));
        }
        return resolvedPromise(new Blob([bytes], { type: mime }));
      } catch (e) {
        return rejectedPromise(new Error('Failed to parse data URL: ' + e.message));
      }
    }

    return fetch(url).then(function (response) {
      if (!response.ok) {
        throw new Error('Failed to fetch image: HTTP ' + response.status);
      }
      return response.blob();
    }, function (err) {
      throw new Error('Failed to fetch image (CORS or network error): ' + err.message);
    });
  }

  /**
   * Copy Blob to clipboard via ClipboardItem
   * @param {Blob} blob
   * @param {string} mimeType
   * @returns {Promise<Blob>}
   */
  function copyImageBlob(blob, mimeType) {
    var type = mimeType || blob.type || 'image/png';
    var itemData = {};
    // Safari requires Promise<Blob> as value
    itemData[type] = Promise.resolve(blob);
    var item = new ClipboardItem(itemData);
    return navigator.clipboard.write([item]).then(function () {
      return blob;
    });
  }

  /**
   * Unified image copy entry (with retry support)
   * @param {Blob|File|HTMLImageElement|HTMLCanvasElement|string} source
   * @param {Object} [options] - Retry options
   * @returns {Promise<Blob>}
   */
  function copyImage(source, options) {
    return fetchImageBlob(source).then(function (blob) {
      return retryOperation(function () {
        return copyImageBlob(blob, blob.type);
      }, options);
    });
  }

  // ========================================
  // 4.2 Rich Text Copy (HTML + Plain Text)
  // ========================================

  /**
   * Copy rich text using ClipboardItem (modern method)
   * @param {string} text - Plain text
   * @param {string} html - HTML content
   * @returns {Promise<{text: string, html: string}>}
   */
  function copyRichTextModern(text, html) {
    var textBlob = new Blob([text], { type: 'text/plain' });
    var htmlBlob = new Blob([html], { type: 'text/html' });
    // Safari requires Promise<Blob> as value
    var item = new ClipboardItem({
      'text/plain': Promise.resolve(textBlob),
      'text/html': Promise.resolve(htmlBlob)
    });
    return navigator.clipboard.write([item]).then(function () {
      return { text: text, html: html };
    });
  }

  /**
   * Copy rich text using execCommand fallback
   * @param {string} text - Plain text
   * @param {string} html - HTML content
   * @param {Element} [container]
   * @returns {Promise<{text: string, html: string}>}
   */
  function copyRichTextLegacy(text, html, container) {
    var el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.opacity = '0';
    el.style.pointerEvents = 'none';

    var isRTL = document.documentElement.getAttribute('dir') === 'rtl';
    el.style[isRTL ? 'right' : 'left'] = '-9999px';

    var yPos = window.pageYOffset || document.documentElement.scrollTop;
    el.style.top = yPos + 'px';

    el.innerHTML = html;

    var containerEl = container || document.body;
    containerEl.appendChild(el);

    var selection = window.getSelection();
    var range = document.createRange();
    range.selectNodeContents(el);
    selection.removeAllRanges();
    selection.addRange(range);

    var succeeded = false;
    try {
      succeeded = document.execCommand('copy');
    } catch (e) {
      succeeded = false;
    }

    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
    clearSelection();

    if (succeeded) {
      return resolvedPromise({ text: text, html: html });
    }
    return rejectedPromise(new Error('execCommand copy failed for rich text'));
  }

  /**
   * Unified rich text copy entry (with retry support)
   * @param {Object} options - { text, html, container?, retries?, retryDelay?, timeout?, signal? }
   * @returns {Promise<{text: string, html: string}>}
   */
  function copyRichText(options) {
    var text = options.text;
    var html = options.html;
    var container = options.container || (_isBrowser ? document.body : null);

    return retryOperation(function () {
      if (isClipboardItemSupported()) {
        return copyRichTextModern(text, html).catch(function () {
          return copyRichTextLegacy(text, html, container);
        });
      }
      return copyRichTextLegacy(text, html, container);
    }, options);
  }

  /**
   * Parse retry config, merge with defaults
   * @param {Object|number} [options] - Config object or retry count
   * @returns {{ retries: number, retryDelay: number, timeout: number, signal: AbortSignal|null }}
   */
  function parseRetryConfig(options) {
    if (typeof options === 'number') {
      return { retries: options, retryDelay: 100, timeout: 0, signal: null };
    }
    var opts = options || {};
    var signal = opts.signal || null;
    // Validate signal
    if (signal && typeof signal.aborted === 'undefined') {
      signal = null;
    }
    return {
      retries: opts.retries != null ? opts.retries : 2,
      retryDelay: opts.retryDelay != null ? opts.retryDelay : 100,
      timeout: opts.timeout != null ? opts.timeout : 0,
      signal: signal
    };
  }

  /**
   * Add timeout to Promise
   * @param {Promise} promise - Original Promise
   * @param {number} ms - Timeout in ms, <=0 means no limit
   * @returns {Promise}
   */
  function withTimeout(promise, ms) {
    if (!ms || ms <= 0) return promise;
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        reject(new Error('Operation timed out after ' + ms + 'ms'));
      }, ms);
      promise.then(function (val) {
        clearTimeout(timer);
        resolve(val);
      }, function (err) {
        clearTimeout(timer);
        reject(err);
      });
    });
  }

  /**
   * Retry mechanism (exponential backoff) with configurable retries, delay, timeout and AbortController support
   * @param {Function} fn - Function that returns a Promise
   * @param {Object|number} config - Config object or max retries (backward compatible)
   */
  function retryOperation(fn, config) {
    var cfg = parseRetryConfig(config);
    var attempt = 0;
    var signal = cfg.signal;

    // Check if already aborted before starting
    if (signal && signal.aborted) {
      return rejectedPromise(new DOMException('Operation aborted', 'AbortError'));
    }

    function tryOnce() {
      // Check abort before each attempt
      if (signal && signal.aborted) {
        return rejectedPromise(new DOMException('Operation aborted', 'AbortError'));
      }

      return fn().catch(function (err) {
        attempt++;
        if (attempt > cfg.retries) {
          return rejectedPromise(err);
        }
        // Check abort before retry delay
        if (signal && signal.aborted) {
          return rejectedPromise(new DOMException('Operation aborted', 'AbortError'));
        }
        var delay = Math.pow(2, attempt - 1) * cfg.retryDelay;
        return new Promise(function (resolve, reject) {
          var onAbort = null;
          var timer = setTimeout(function () {
            // Clean up abort listener before resolving to prevent memory leak
            if (onAbort && signal) {
              signal.removeEventListener('abort', onAbort);
            }
            resolve();
          }, delay);
          // Listen for abort during delay
          if (signal) {
            onAbort = function () {
              clearTimeout(timer);
              reject(new DOMException('Operation aborted', 'AbortError'));
            };
            signal.addEventListener('abort', onAbort, { once: true });
          }
        }).then(tryOnce);
      });
    }

    return withTimeout(tryOnce(), cfg.timeout);
  }

  // ========================================
  // 5. Node.js Adapter
  // ========================================

  /**
   * Copy text to system clipboard in Node.js environment
   */
  function nodeClipboardCopy(text) {
    if (!_isNode) {
      return rejectedPromise(new Error('Not running in Node.js environment'));
    }

    return new Promise(function (resolve, reject) {
      var platform = process.platform;
      var cmd, args;

      if (platform === 'win32') {
        // Use PowerShell EncodedCommand to avoid stdin encoding issues with Unicode/Chinese text.
        // Base64-encode the text and pass it directly, bypassing stdin entirely.
        var psScript = 'Set-Clipboard -Value ([System.Text.Encoding]::UTF8.GetString([Convert]::FromBase64String(\'' +
          Buffer.from(text, 'utf8').toString('base64') + '\')))';
        cmd = 'powershell';
        args = ['-NoProfile', '-NonInteractive', '-EncodedCommand',
          Buffer.from(psScript, 'utf16le').toString('base64')];
      } else if (platform === 'darwin') {
        cmd = 'pbcopy';
        args = [];
      } else {
        // Linux: try xclip
        cmd = 'xclip';
        args = ['-selection', 'clipboard'];
      }

      try {
        var spawn = require('child_process').spawn;
        var proc = spawn(cmd, args, { stdio: ['pipe', 'ignore', 'ignore'] });
        var finished = false;

        proc.on('error', function (err) {
          if (!finished) {
            finished = true;
            if (platform !== 'win32' && platform !== 'darwin') {
              // Linux: fallback to xsel
              nodeClipboardCopyFallback(text).then(resolve, reject);
            } else {
              reject(new Error('Clipboard command failed: ' + cmd + ' - ' + err.message));
            }
          }
        });

        proc.on('close', function (code) {
          if (!finished) {
            finished = true;
            if (code === 0) {
              resolve(text);
            } else {
              reject(new Error('Clipboard command exited with code: ' + code));
            }
          }
        });

        // For non-Windows, write text via stdin
        if (platform !== 'win32') {
          proc.stdin.write(text);
          proc.stdin.end();
        } else {
          // Windows: close stdin immediately (text is passed via EncodedCommand)
          proc.stdin.end();
        }
      } catch (e) {
        reject(new Error('Failed to spawn clipboard process: ' + e.message));
      }
    });
  }

  /**
   * Linux fallback: use xsel
   */
  function nodeClipboardCopyFallback(text) {
    return new Promise(function (resolve, reject) {
      try {
        var spawn = require('child_process').spawn;
        var proc = spawn('xsel', ['--clipboard', '--input'], { stdio: ['pipe', 'ignore', 'ignore'] });
        var finished = false;

        proc.on('error', function (err) {
          if (!finished) {
            finished = true;
            reject(new Error('No clipboard command available. Install xclip or xsel. ' + err.message));
          }
        });

        proc.on('close', function (code) {
          if (!finished) {
            finished = true;
            if (code === 0) {
              resolve(text);
            } else {
              reject(new Error('xsel exited with code: ' + code));
            }
          }
        });

        proc.stdin.write(text);
        proc.stdin.end();
      } catch (e) {
        reject(new Error('Failed to spawn xsel: ' + e.message));
      }
    });
  }

  /**
   * Read text from system clipboard in Node.js environment
   * @returns {Promise<string>}
   */
  function nodeClipboardRead() {
    if (!_isNode) {
      return rejectedPromise(new Error('Not running in Node.js environment'));
    }

    return new Promise(function (resolve, reject) {
      var platform = process.platform;
      var cmd, args;

      if (platform === 'win32') {
        // Use EncodedCommand with explicit UTF-8 output encoding
        var psScript = '[Console]::OutputEncoding = [System.Text.Encoding]::UTF8; Get-Clipboard';
        cmd = 'powershell';
        args = ['-NoProfile', '-NonInteractive', '-EncodedCommand',
          Buffer.from(psScript, 'utf16le').toString('base64')];
      } else if (platform === 'darwin') {
        cmd = 'pbpaste';
        args = [];
      } else {
        // Linux: try xclip
        cmd = 'xclip';
        args = ['-selection', 'clipboard', '-o'];
      }

      try {
        var spawn = require('child_process').spawn;
        var proc = spawn(cmd, args, { stdio: ['ignore', 'pipe', 'pipe'] });
        var output = '';
        var errOutput = '';
        var finished = false;

        proc.stdout.on('data', function (chunk) {
          output += chunk.toString('utf8');
        });

        proc.stderr.on('data', function (chunk) {
          errOutput += chunk.toString('utf8');
        });

        proc.on('error', function (err) {
          if (!finished) {
            finished = true;
            if (platform !== 'win32' && platform !== 'darwin') {
              // Linux: fallback to xsel
              nodeClipboardReadFallback().then(resolve, reject);
            } else {
              reject(new Error('Clipboard read command failed: ' + cmd + ' - ' + err.message));
            }
          }
        });

        proc.on('close', function (code) {
          if (!finished) {
            finished = true;
            if (code === 0) {
              // Trim trailing newline
              resolve(output.replace(/\r?\n$/, ''));
            } else {
              reject(new Error('Clipboard read command exited with code: ' + code +
                (errOutput ? ' - ' + errOutput.trim() : '')));
            }
          }
        });
      } catch (e) {
        reject(new Error('Failed to spawn clipboard read process: ' + e.message));
      }
    });
  }

  /**
   * Linux read fallback: use xsel
   * @returns {Promise<string>}
   */
  function nodeClipboardReadFallback() {
    return new Promise(function (resolve, reject) {
      try {
        var spawn = require('child_process').spawn;
        var proc = spawn('xsel', ['--clipboard', '--output'], { stdio: ['ignore', 'pipe', 'pipe'] });
        var output = '';
        var errOutput = '';
        var finished = false;

        proc.stdout.on('data', function (chunk) {
          output += chunk.toString('utf8');
        });

        proc.stderr.on('data', function (chunk) {
          errOutput += chunk.toString('utf8');
        });

        proc.on('error', function (err) {
          if (!finished) {
            finished = true;
            reject(new Error('No clipboard read command available. Install xclip or xsel. ' + err.message));
          }
        });

        proc.on('close', function (code) {
          if (!finished) {
            finished = true;
            if (code === 0) {
              resolve(output.replace(/\r?\n$/, ''));
            } else {
              reject(new Error('xsel read exited with code: ' + code +
                (errOutput ? ' - ' + errOutput.trim() : '')));
            }
          }
        });
      } catch (e) {
        reject(new Error('Failed to spawn xsel for read: ' + e.message));
      }
    });
  }

  // ========================================
  // 6. Promise Helpers
  // ========================================

  function resolvedPromise(val) {
    return Promise.resolve(val);
  }

  function rejectedPromise(err) {
    return Promise.reject(err);
  }

  /**
   * Query clipboard permission status
   * @param {string} permissionName - Permission name ('clipboard-read' or 'clipboard-write')
   * @returns {Promise<{ state: string }>}
   */
  function queryClipboardPermission(permissionName) {
    if (!_isBrowser || !navigator.permissions ||
        !_isFunction(navigator.permissions.query)) {
      return resolvedPromise({ state: 'prompt' });
    }
    try {
      return navigator.permissions.query({ name: permissionName }).then(function (result) {
        return { state: result.state };
      }).catch(function () {
        return { state: 'prompt' };
      });
    } catch (e) {
      return resolvedPromise({ state: 'prompt' });
    }
  }

  // ========================================
  // 7. DOM Attribute Binding & Event Delegation
  // ========================================

  /**
   * Get element's data-nc-* attribute value
   */
  function getAttr(name, element) {
    var attr = 'data-nc-' + name;
    if (element.hasAttribute(attr)) {
      return element.getAttribute(attr);
    }
    return undefined;
  }

  /**
   * Find closest ancestor matching selector (including self)
   */
  function closest(element, selector) {
    if (element.closest) {
      return element.closest(selector);
    }
    // Fallback
    var el = element;
    while (el && el.nodeType === 1) {
      if (matches(el, selector)) return el;
      el = el.parentNode;
    }
    return null;
  }

  function matches(el, selector) {
    var fn = el.matches || el.matchesSelector || el.msMatchesSelector ||
      el.mozMatchesSelector || el.webkitMatchesSelector || el.oMatchesSelector;
    return fn ? fn.call(el, selector) : false;
  }

  /**
   * Event delegation - listen on container, delegate to matching child elements
   */
  function delegateEvent(container, selector, eventType, handler) {
    function listener(e) {
      var target = closest(e.target, selector);
      if (target) {
        e.delegateTarget = target;
        handler.call(target, e);
      }
    }

    container.addEventListener(eventType, listener, false);

    return {
      destroy: function () {
        container.removeEventListener(eventType, listener, false);
      }
    };
  }

  /**
   * Bind event listener - supports selector string, Element, NodeList
   * Note: Throws synchronously for invalid arguments (programming error, not runtime error)
   */
  function bindListener(target, eventType, handler) {
    if (_isString(target)) {
      // Selector -> event delegation
      return delegateEvent(document.body, target, eventType, handler);
    }

    if (_isElement(target)) {
      target.addEventListener(eventType, handler, false);
      return {
        destroy: function () {
          target.removeEventListener(eventType, handler, false);
        }
      };
    }

    if (_isNodeList(target)) {
      var destroyers = [];
      for (var i = 0; i < target.length; i++) {
        (function (el) {
          el.addEventListener(eventType, handler, false);
          destroyers.push(function () {
            el.removeEventListener(eventType, handler, false);
          });
        })(target[i]);
      }
      return {
        destroy: function () {
          for (var j = 0; j < destroyers.length; j++) {
            destroyers[j]();
          }
        }
      };
    }

    throw new TypeError('First argument must be a String selector, HTMLElement, or NodeList');
  }

  /**
   * Extract data from paste event's clipboardData
   * @param {DataTransfer} clipboardData - Event's clipboardData object
   * @returns {{ text: string, html: string, files: File[] }}
   */
  function parsePasteData(clipboardData) {
    var result = { text: '', html: '', files: [] };
    if (!clipboardData) return result;

    try { result.text = clipboardData.getData('text/plain') || ''; } catch (e) { /* ignore */ }
    try { result.html = clipboardData.getData('text/html') || ''; } catch (e) { /* ignore */ }
    try {
      if (clipboardData.files && clipboardData.files.length > 0) {
        result.files = Array.prototype.slice.call(clipboardData.files);
      }
    } catch (e) { /* ignore */ }

    return result;
  }

  /**
   * Bind paste event listener
   * @param {string|Element|null} target - Selector, element, or null (defaults to document)
   * @param {Function} callback - Callback, receives { text, html, files, originalEvent }
   * @returns {{ destroy: Function }}
   */
  function bindPasteListener(target, callback) {
    if (!_isFunction(callback)) {
      throw new TypeError('Callback must be a function');
    }

    function pasteHandler(e) {
      var delegateEl = e.delegateTarget || e.currentTarget || e.target;
      var data = parsePasteData(e.clipboardData);
      data.originalEvent = e;
      data.trigger = delegateEl;
      callback(data);
    }

    // null / undefined -> document
    if (target == null) {
      document.addEventListener('paste', pasteHandler, false);
      return {
        destroy: function () {
          document.removeEventListener('paste', pasteHandler, false);
        }
      };
    }

    // Selector string -> event delegation
    if (_isString(target)) {
      return delegateEvent(document.body, target, 'paste', pasteHandler);
    }

    // Element
    if (_isElement(target)) {
      target.addEventListener('paste', pasteHandler, false);
      return {
        destroy: function () {
          target.removeEventListener('paste', pasteHandler, false);
        }
      };
    }

    throw new TypeError('Target must be a String selector, HTMLElement, or null');
  }

  // ========================================
  // 8. NowClipboard Main Class
  // ========================================

  /**
   * NowClipboard constructor
   * @param {string|Element|NodeList} trigger - Trigger element or selector
   * @param {Object} [options] - Options
   * @param {Function|string} [options.action] - Action type (copy/cut)
   * @param {Function} [options.target] - Function to get target element
   * @param {Function|string} [options.text] - Function or string to get copy text
   * @param {Element} [options.container] - Container element (defaults to document.body)
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel operations
   */
  function NowClipboard(trigger, options) {
    if (!(this instanceof NowClipboard)) {
      return new NowClipboard(trigger, options);
    }

    EventEmitter.call(this);
    this._destroyed = false;
    this._listener = null;

    this.resolveOptions(options || {});

    if (_isBrowser && trigger) {
      this.listenClick(trigger);
    }
  }

  // Inherit EventEmitter
  NowClipboard.prototype = Object.create(EventEmitter.prototype);
  NowClipboard.prototype.constructor = NowClipboard;

  /**
   * Check if instance has been destroyed, throw if so
   */
  NowClipboard.prototype._checkDestroyed = function () {
    if (this._destroyed) {
      throw new Error('NowClipboard instance has been destroyed. Create a new instance.');
    }
  };

  /**
   * Resolve options
   */
  NowClipboard.prototype.resolveOptions = function (opts) {
    this.action = _isFunction(opts.action) ? opts.action : this.defaultAction;
    this.target = _isFunction(opts.target) ? opts.target : this.defaultTarget;
    this.text = _isFunction(opts.text) ? opts.text : this.defaultText;
    this.container = _isElement(opts.container) ? opts.container : (_isBrowser ? document.body : null);
    this.retries = opts.retries != null ? opts.retries : 2;
    this.retryDelay = opts.retryDelay != null ? opts.retryDelay : 100;
    this.timeout = opts.timeout != null ? opts.timeout : 0;
    this.signal = opts.signal || null;
  };

  /**
   * Bind click event
   */
  NowClipboard.prototype.listenClick = function (trigger) {
    var self = this;
    this._listener = bindListener(trigger, 'click', function (e) {
      self.onClick(e);
    });
  };

  /**
   * Click event handler
   */
  NowClipboard.prototype.onClick = function (e) {
    this._checkDestroyed();

    var trigger = e.delegateTarget || e.currentTarget;
    var actionName = this.action(trigger) || 'copy';
    var targetEl = this.target(trigger);
    var text = this.text(trigger);
    var self = this;

    // Validate action type
    if (actionName !== 'copy' && actionName !== 'cut') {
      self.emit('error', {
        action: actionName,
        trigger: trigger,
        error: new Error('Invalid action "' + actionName + '", use "copy" or "cut"'),
        clearSelection: clearSelection
      });
      return;
    }

    // Determine operation
    var operationPromise;

    // Check if rich text copy mode (data-nc-html="true")
    var isRichCopy = getAttr('html', trigger) === 'true';

    // Build retry options with instance-level signal
    var retryOpts = { container: self.container, retries: self.retries, retryDelay: self.retryDelay, timeout: self.timeout, signal: self.signal };

    if (text) {
      // Text specified, copy directly
      operationPromise = copyText(text, retryOpts);
    } else if (targetEl) {
      if (actionName === 'cut') {
        // Validate element for cut operation
        if (targetEl.hasAttribute('readonly') || targetEl.hasAttribute('disabled')) {
          self.emit('error', {
            action: actionName,
            trigger: trigger,
            error: new Error('Cannot cut from elements with "readonly" or "disabled" attributes'),
            clearSelection: clearSelection
          });
          return;
        }
        operationPromise = performCut(targetEl, retryOpts);
      } else if (isRichCopy) {
        // Rich text copy: copy HTML and plain text simultaneously
        var richHtml = targetEl.innerHTML;
        var richText = targetEl.textContent || targetEl.innerText || '';
        operationPromise = copyRichText({
          text: richText,
          html: richHtml,
          container: self.container,
          retries: self.retries,
          retryDelay: self.retryDelay,
          timeout: self.timeout,
          signal: self.signal
        }).then(function (result) {
          return result.text;
        });
      } else {
        // Copy is allowed from disabled elements (only cut is restricted)
        operationPromise = copyFromElement(targetEl, retryOpts);
      }
    } else {
      self.emit('error', {
        action: actionName,
        trigger: trigger,
        error: new Error('No text or target specified'),
        clearSelection: clearSelection
      });
      return;
    }

    operationPromise.then(function (copiedText) {
      self.emit('success', {
        action: actionName,
        text: copiedText,
        trigger: trigger,
        clearSelection: clearSelection
      });
    }).catch(function (err) {
      self.emit('error', {
        action: actionName,
        trigger: trigger,
        error: err,
        clearSelection: clearSelection
      });
    });
  };

  /**
   * Override EventEmitter methods to check destroyed state
   */
  var _originalOn = NowClipboard.prototype.on;
  NowClipboard.prototype.on = function () {
    this._checkDestroyed();
    return _originalOn.apply(this, arguments);
  };

  var _originalOnce = NowClipboard.prototype.once;
  NowClipboard.prototype.once = function () {
    this._checkDestroyed();
    return _originalOnce.apply(this, arguments);
  };

  var _originalOff = NowClipboard.prototype.off;
  NowClipboard.prototype.off = function () {
    this._checkDestroyed();
    return _originalOff.apply(this, arguments);
  };

  /**
   * Default action: get from data-nc-action attribute
   */
  NowClipboard.prototype.defaultAction = function (trigger) {
    return getAttr('action', trigger) || 'copy';
  };

  /**
   * Default target: get from data-nc-target attribute
   */
  NowClipboard.prototype.defaultTarget = function (trigger) {
    var selector = getAttr('target', trigger);
    if (selector) {
      return document.querySelector(selector);
    }
    return undefined;
  };

  /**
   * Default text: get from data-nc-text attribute
   */
  NowClipboard.prototype.defaultText = function (trigger) {
    return getAttr('text', trigger);
  };

  /**
   * Destroy instance, clean up all resources
   */
  NowClipboard.prototype.destroy = function () {
    if (this._destroyed) return;
    this._destroyed = true;

    if (this._listener) {
      this._listener.destroy();
      this._listener = null;
    }

    this.removeAllListeners();
    this.action = null;
    this.target = null;
    this.text = null;
    this.container = null;
    this.signal = null;
  };

  // ========================================
  // 9. Static Methods
  // ========================================

  /**
   * Static copy method - copy text directly
   * @param {string} text - Text to copy
   * @param {Object} [options] - Options (retries/retryDelay/timeout/signal/container)
   * @returns {Promise<string>}
   */
  NowClipboard.copy = function (text, options) {
    if (!_isString(text)) {
      return rejectedPromise(new TypeError('NowClipboard.copy() expects a string argument'));
    }
    return copyText(text, options);
  };

  /**
   * Static cut method - cut element content
   * @param {Element} element - Target element
   * @param {Object} [options] - Retry options (retries/retryDelay/timeout/signal)
   * @returns {Promise<string>}
   */
  NowClipboard.cut = function (element, options) {
    if (!_isBrowser) {
      return rejectedPromise(new Error('NowClipboard.cut() is only available in browser environment'));
    }
    if (!_isElement(element)) {
      return rejectedPromise(new TypeError('NowClipboard.cut() expects an HTMLElement argument'));
    }
    return performCut(element, options);
  };

  /**
   * Static read method - read clipboard text
   * @param {Object} [options] - Options (retries/retryDelay/timeout/signal)
   * @returns {Promise<string>}
   */
  NowClipboard.read = function (options) {
    return readText(options);
  };

  /**
   * Static readRich method - read clipboard with rich content (text, html, images)
   * Requires HTTPS + modern browser with ClipboardItem API
   * @param {Object} [options] - Options (retries/retryDelay/timeout/signal)
   * @returns {Promise<{text: string, html: string, images: Blob[]}>}
   */
  NowClipboard.readRich = function (options) {
    if (!_isBrowser) {
      return rejectedPromise(new Error('NowClipboard.readRich() is only available in browser environment'));
    }
    if (!isClipboardReadSupported()) {
      return rejectedPromise(new Error('clipboard.read() API not supported. Requires HTTPS and a modern browser'));
    }

    return retryOperation(function () {
      return navigator.clipboard.read().then(function (clipItems) {
        var result = { text: '', html: '', images: [] };
        var promises = [];

        for (var i = 0; i < clipItems.length; i++) {
          var item = clipItems[i];
          for (var j = 0; j < item.types.length; j++) {
            (function (type) {
              if (type === 'text/plain') {
                promises.push(item.getType(type).then(function (blob) {
                  return blob.text();
                }).then(function (text) {
                  result.text = text;
                }));
              } else if (type === 'text/html') {
                promises.push(item.getType(type).then(function (blob) {
                  return blob.text();
                }).then(function (html) {
                  result.html = html;
                }));
              } else if (type.indexOf('image/') === 0) {
                promises.push(item.getType(type).then(function (blob) {
                  result.images.push(blob);
                }));
              }
            })(item.types[j]);
          }
        }

        return Promise.all(promises).then(function () {
          return result;
        });
      });
    }, options);
  };

  /**
   * Check if current environment supports clipboard operations
   * @param {string|string[]} [actions] - Actions to check (default: ['copy', 'cut'])
   * @returns {boolean}
   */
  NowClipboard.checkSupport = function (actions) {
    if (_isNode) return true; // Node.js supported via system commands

    if (!_isBrowser) return false;

    // Normalize actions parameter
    var actionList = actions || ['copy', 'cut'];
    if (_isString(actionList)) actionList = [actionList];

    // Modern Clipboard API available — check specific capabilities
    if (isClipboardAPIAvailable()) {
      for (var i = 0; i < actionList.length; i++) {
        var action = actionList[i];
        if (action === 'read') {
          // readText / read requires separate check
          if (typeof navigator.clipboard.readText !== 'function') return false;
        }
        // copy, cut, and write are supported if writeText exists (checked by isClipboardAPIAvailable)
      }
      return true;
    }

    // Check execCommand support
    var supported = true;
    for (var i = 0; i < actionList.length; i++) {
      supported = supported && isExecCommandSupported(actionList[i]);
    }
    return supported;
  };

  /**
   * Query clipboard permission status
   * @param {string} name - Permission name: 'read' or 'write'
   * @returns {Promise<{ state: string }>} state: 'granted'|'denied'|'prompt'
   */
  NowClipboard.queryPermission = function (name) {
    if (name !== 'read' && name !== 'write') {
      return rejectedPromise(new TypeError('queryPermission() expects "read" or "write"'));
    }
    if (_isNode) {
      return resolvedPromise({ state: 'granted' });
    }
    return queryClipboardPermission('clipboard-' + name);
  };

  /**
   * Static paste listener - listen to paste events
   * @param {string|Element|null} target - Selector, element, or null (defaults to document)
   * @param {Function} callback - Callback, receives { text, html, files, originalEvent, trigger }
   * @returns {{ destroy: Function }}
   */
  NowClipboard.onPaste = function (target, callback) {
    if (!_isBrowser) {
      // Return a destroyable stub so callers can safely call .destroy()
      // The returned object does nothing but prevents TypeError
      return {
        destroy: function () {
          // No-op: onPaste not supported outside browser
        }
      };
    }
    return bindPasteListener(target, callback);
  };

  /**
   * Static onChange listener - polls clipboard for changes
   * @param {Function} callback - Callback, receives { text: string }
   * @param {number} [interval=1000] - Polling interval in ms
   * @returns {{ destroy: Function }}
   */
  NowClipboard.onChange = function (callback, interval) {
    if (!_isBrowser) {
      // Return a destroyable stub so callers can safely call .destroy()
      return {
        destroy: function () {
          // No-op: onChange not supported outside browser
        }
      };
    }
    if (!_isFunction(callback)) {
      throw new TypeError('NowClipboard.onChange() expects a callback function');
    }

    var pollInterval = interval || 1000;
    var lastText = null;
    var timer = null;
    var destroyed = false;
    var initialized = false;

    function poll() {
      if (destroyed) return;
      readText().then(function (text) {
        if (destroyed) return;
        if (!initialized) {
          // First read: initialize without triggering callback
          lastText = text;
          initialized = true;
        } else if (text !== lastText) {
          lastText = text;
          try {
            callback({ text: text });
          } catch (e) {
            // Don't let callback errors break the polling loop, but warn for debugging
            if (typeof console !== 'undefined' && console.warn) {
              console.warn('NowClipboard onChange callback error:', e);
            }
          }
        }
      }).catch(function () {
        // Silently ignore read errors (e.g. page not focused, permission denied)
      });
    }

    // Initial read
    poll();

    // Start polling
    timer = setInterval(poll, pollInterval);

    return {
      destroy: function () {
        destroyed = true;
        if (timer !== null) {
          clearInterval(timer);
          timer = null;
        }
      }
    };
  };

  /**
   * Copy image to clipboard (browser only, requires HTTPS + modern browser)
   * @param {Blob|File|HTMLImageElement|HTMLCanvasElement|string} source - Image source
   * @param {Object} [options] - Retry options (retries/retryDelay/timeout/signal)
   * @returns {Promise<Blob>}
   */
  NowClipboard.copyImage = function (source, options) {
    if (!_isBrowser) {
      return rejectedPromise(new Error('NowClipboard.copyImage() is only available in browser environment'));
    }
    if (!isClipboardItemSupported()) {
      return rejectedPromise(new Error('ClipboardItem API not supported. Requires HTTPS and a modern browser'));
    }
    return copyImage(source, options);
  };

  /**
   * Copy any Blob to clipboard (browser only)
   * @param {Blob} blob - Blob data
   * @param {string} [mimeType] - MIME type, defaults to blob.type
   * @param {Object} [options] - Retry options (retries/retryDelay/timeout/signal)
   * @returns {Promise<Blob>}
   */
  NowClipboard.copyBlob = function (blob, mimeType, options) {
    if (!_isBrowser) {
      return rejectedPromise(new Error('NowClipboard.copyBlob() is only available in browser environment'));
    }
    if (!isClipboardItemSupported()) {
      return rejectedPromise(new Error('ClipboardItem API not supported. Requires HTTPS and a modern browser'));
    }
    if (!(blob instanceof Blob)) {
      return rejectedPromise(new TypeError('NowClipboard.copyBlob() expects a Blob argument'));
    }
    var type = mimeType || blob.type;
    return retryOperation(function () {
      return copyImageBlob(blob, type);
    }, options);
  };

  /**
   * Copy rich text (HTML + plain text) to clipboard (browser only)
   * @param {Object} options - { text: string, html: string, container?, retries?, retryDelay?, timeout?, signal? }
   * @returns {Promise<{text: string, html: string}>}
   */
  NowClipboard.copyRich = function (options) {
    if (!options || !_isString(options.text) || !_isString(options.html)) {
      return rejectedPromise(new TypeError('NowClipboard.copyRich() requires { text: string, html: string }'));
    }
    if (!_isBrowser) {
      return rejectedPromise(new Error('NowClipboard.copyRich() is only available in browser environment'));
    }
    return copyRichText(options);
  };

  /**
   * Write text to clipboard (works in both browser and Node.js)
   * Alias for copy() in browser, uses system commands in Node.js
   * @param {string} text - Text to write
   * @param {Object} [options] - Retry options (retries/retryDelay/timeout/signal/container)
   * @returns {Promise<string>}
   */
  NowClipboard.write = function (text, options) {
    if (!_isString(text)) {
      return rejectedPromise(new TypeError('NowClipboard.write() expects a string argument'));
    }
    return copyText(text, options);
  };

  /**
   * Write image to clipboard (works in both browser and Node.js)
   * In browser: uses ClipboardItem API (requires HTTPS + modern browser)
   * In Node.js: uses system commands (macOS pbcopy with PNG, Linux xclip, Windows PowerShell)
   * @param {Blob|File|HTMLImageElement|HTMLCanvasElement|string|Buffer} source - Image source
   * @param {Object} [options] - Retry options (retries/retryDelay/timeout/signal)
   * @returns {Promise<Blob>}
   */
  NowClipboard.writeImage = function (source, options) {
    if (_isBrowser) {
      // Browser: delegate to copyImage
      if (!isClipboardItemSupported()) {
        return rejectedPromise(new Error('ClipboardItem API not supported. Requires HTTPS and a modern browser'));
      }
      return copyImage(source, options);
    }

    if (_isNode) {
      return nodeClipboardWriteImage(source, options);
    }

    return rejectedPromise(new Error('NowClipboard.writeImage() is not supported in this environment'));
  };

  /**
   * Node.js: write image to system clipboard
   * Supports: Buffer, Blob (Node 18+), file path string
   * @param {Buffer|Blob|string} source - Image data or file path
   * @param {Object} [options] - Retry options
   * @returns {Promise<Blob>}
   */
  function nodeClipboardWriteImage(source, options) {
    return new Promise(function (resolve, reject) {
      var platform = process.platform;

      // Resolve image buffer
      var imagePromise;
      if (typeof Buffer !== 'undefined' && source instanceof Buffer) {
        imagePromise = resolvedPromise(source);
      } else if (typeof Blob !== 'undefined' && source instanceof Blob) {
        // Node 18+ Blob
        imagePromise = source.arrayBuffer().then(function (ab) {
          return Buffer.from(ab);
        });
      } else if (_isString(source)) {
        // File path - read file
        try {
          var fs = require('fs');
          imagePromise = new Promise(function (res, rej) {
            fs.readFile(source, function (err, data) {
              if (err) rej(err);
              else res(data);
            });
          });
        } catch (e) {
          reject(new Error('Failed to read image file: ' + e.message));
          return;
        }
      } else {
        reject(new TypeError('writeImage() expects Buffer, Blob, or file path string in Node.js'));
        return;
      }

      imagePromise.then(function (buffer) {
        var cmd, args, stdinData;

        if (platform === 'darwin') {
          // macOS: pbcopy accepts PNG via stdin
          cmd = 'pbcopy';
          args = [];
          stdinData = buffer;
        } else if (platform === 'linux') {
          // Linux: xclip with image/png type
          cmd = 'xclip';
          args = ['-selection', 'clipboard', '-t', 'image/png'];
          stdinData = buffer;
        } else if (platform === 'win32') {
          // Windows: PowerShell Set-Clipboard doesn't support images directly
          // Use a PowerShell script to create a temp file and set the clipboard image
          var psScript = [
            'Add-Type -AssemblyName System.Windows.Forms',
            'Add-Type -AssemblyName System.Drawing',
            '$ms = New-Object System.IO.MemoryStream(,([Convert]::FromBase64String(\'' + buffer.toString('base64') + '\')))',
            '$img = [System.Drawing.Image]::FromStream($ms)',
            '[System.Windows.Forms.Clipboard]::SetImage($img)',
            '$img.Dispose()',
            '$ms.Dispose()'
          ].join(';');
          cmd = 'powershell';
          args = ['-NoProfile', '-NonInteractive', '-EncodedCommand',
            Buffer.from(psScript, 'utf16le').toString('base64')];
          stdinData = null;
        } else {
          reject(new Error('writeImage() is not supported on platform: ' + platform));
          return;
        }

        try {
          var spawn = require('child_process').spawn;
          var proc;
          if (stdinData !== null) {
            proc = spawn(cmd, args, { stdio: ['pipe', 'ignore', 'pipe'] });
            proc.stdin.write(stdinData);
            proc.stdin.end();
          } else {
            proc = spawn(cmd, args, { stdio: ['ignore', 'ignore', 'pipe'] });
          }

          var errOutput = '';
          var finished = false;

          if (proc.stderr) {
            proc.stderr.on('data', function (chunk) {
              errOutput += chunk.toString('utf8');
            });
          }

          proc.on('error', function (err) {
            if (!finished) {
              finished = true;
              var fallbackMsg = platform === 'linux' ? ' Install xclip or xsel.' : '';
              reject(new Error('writeImage() command failed: ' + cmd + ' - ' + err.message + fallbackMsg));
            }
          });

          proc.on('close', function (code) {
            if (!finished) {
              finished = true;
              if (code === 0) {
                // Return a Blob-like object for consistency
                try {
                  var resultBlob = new Blob([buffer], { type: 'image/png' });
                  resolve(resultBlob);
                } catch (e) {
                  // Node.js < 18 without Blob support
                  resolve(buffer);
                }
              } else {
                reject(new Error('writeImage() command exited with code: ' + code +
                  (errOutput ? ' - ' + errOutput.trim() : '')));
              }
            }
          });
        } catch (e) {
          reject(new Error('Failed to spawn writeImage process: ' + e.message));
        }
      }).catch(function (err) {
        reject(err);
      });
    });
  }

  /**
   * Write multiple formats to clipboard at once (browser only)
   * @param {Object} formats - Map of MIME type to string or Blob, e.g. { 'text/plain': 'hello', 'text/html': '<b>hello</b>' }
   * @param {Object} [options] - Retry options (retries/retryDelay/timeout/signal)
   * @returns {Promise<Object>} - The formats object that was written
   */
  NowClipboard.writeFormats = function (formats, options) {
    if (!_isBrowser) {
      return rejectedPromise(new Error('NowClipboard.writeFormats() is only available in browser environment'));
    }
    if (!isClipboardItemSupported()) {
      return rejectedPromise(new Error('ClipboardItem API not supported. Requires HTTPS and a modern browser'));
    }
    if (!formats || typeof formats !== 'object') {
      return rejectedPromise(new TypeError('NowClipboard.writeFormats() expects an object of MIME types'));
    }

    var keys = Object.keys(formats);
    if (keys.length === 0) {
      return rejectedPromise(new TypeError('NowClipboard.writeFormats() requires at least one MIME type'));
    }

    return retryOperation(function () {
      var itemData = {};
      for (var i = 0; i < keys.length; i++) {
        var mime = keys[i];
        var value = formats[mime];
        if (value instanceof Blob) {
          itemData[mime] = Promise.resolve(value);
        } else {
          // Convert string to Blob
          itemData[mime] = Promise.resolve(new Blob([value], { type: mime }));
        }
      }
      var item = new ClipboardItem(itemData);
      return navigator.clipboard.write([item]).then(function () {
        return formats;
      });
    }, options);
  };

  // ========================================
  // 10. ClipboardHistory
  // ========================================

  /**
   * ClipboardHistory - Records clipboard changes automatically
   * @constructor
   * @param {Object} [options] - Configuration
   * @param {number} [options.maxSize=50] - Maximum number of history entries
   * @param {string} [options.storage='memory'] - Storage type: 'memory' | 'localStorage' | 'sessionStorage'
   * @param {string} [options.storageKey='nowclipboard_history'] - Key for storage
   * @param {number} [options.pollInterval=1000] - Polling interval for onChange (ms)
   */
  function ClipboardHistory(options) {
    if (!(this instanceof ClipboardHistory)) {
      return new ClipboardHistory(options);
    }

    var opts = options || {};
    this._maxSize = opts.maxSize || 50;
    this._storageType = opts.storage || 'memory';
    this._storageKey = opts.storageKey || 'nowclipboard_history';
    this._pollInterval = opts.pollInterval || 1000;
    this._entries = [];
    this._watcher = null;
    this._destroyed = false;

    // Load from storage if persistent
    if (this._storageType === 'localStorage' || this._storageType === 'sessionStorage') {
      this._loadFromStorage();
    }
  }

  /**
   * Start monitoring clipboard changes
   * @returns {ClipboardHistory} this
   */
  ClipboardHistory.prototype.start = function () {
    if (this._destroyed) throw new Error('ClipboardHistory has been destroyed');
    if (this._watcher) return this; // Already started

    var self = this;
    this._watcher = NowClipboard.onChange(function (data) {
      self._addEntry(data.text);
    }, this._pollInterval);

    return this;
  };

  /**
   * Stop monitoring clipboard changes
   * @returns {ClipboardHistory} this
   */
  ClipboardHistory.prototype.stop = function () {
    if (this._watcher) {
      this._watcher.destroy();
      this._watcher = null;
    }
    return this;
  };

  /**
   * Add an entry to history (internal)
   */
  ClipboardHistory.prototype._addEntry = function (text) {
    if (this._destroyed) return;
    var entry = {
      text: text,
      timestamp: Date.now(),
      type: 'text'
    };

    this._entries.push(entry);

    // Enforce max size
    while (this._entries.length > this._maxSize) {
      this._entries.shift();
    }

    this._saveToStorage();
  };

  /**
   * Get all history entries
   * @returns {Array<{text: string, timestamp: number, type: string}>}
   */
  ClipboardHistory.prototype.list = function () {
    if (this._destroyed) throw new Error('ClipboardHistory has been destroyed');
    return this._entries.slice();
  };

  /**
   * Search history entries by keyword
   * @param {string} keyword - Search keyword
   * @returns {Array<{text: string, timestamp: number, type: string}>}
   */
  ClipboardHistory.prototype.search = function (keyword) {
    if (this._destroyed) throw new Error('ClipboardHistory has been destroyed');
    if (!_isString(keyword)) {
      throw new TypeError('search() expects a string keyword');
    }
    var lower = keyword.toLowerCase();
    return this._entries.filter(function (entry) {
      return entry.text.toLowerCase().indexOf(lower) !== -1;
    });
  };

  /**
   * Get the most recent entry
   * @returns {{text: string, timestamp: number, type: string}|null}
   */
  ClipboardHistory.prototype.latest = function () {
    if (this._destroyed) throw new Error('ClipboardHistory has been destroyed');
    return this._entries.length > 0 ? this._entries[this._entries.length - 1] : null;
  };

  /**
   * Clear all history entries
   */
  ClipboardHistory.prototype.clear = function () {
    if (this._destroyed) throw new Error('ClipboardHistory has been destroyed');
    this._entries = [];
    this._saveToStorage();
  };

  /**
   * Get current history size
   * @returns {number}
   */
  ClipboardHistory.prototype.size = function () {
    return this._entries.length;
  };

  /**
   * Load entries from Web Storage
   */
  ClipboardHistory.prototype._loadFromStorage = function () {
    try {
      var storage = this._storageType === 'localStorage' ? localStorage : sessionStorage;
      var data = storage.getItem(this._storageKey);
      if (data) {
        this._entries = JSON.parse(data);
        // Enforce max size after loading
        while (this._entries.length > this._maxSize) {
          this._entries.shift();
        }
      }
    } catch (e) {
      this._entries = [];
    }
  };

  /**
   * Save entries to Web Storage
   */
  ClipboardHistory.prototype._saveToStorage = function () {
    if (this._storageType === 'memory') return;
    try {
      var storage = this._storageType === 'localStorage' ? localStorage : sessionStorage;
      storage.setItem(this._storageKey, JSON.stringify(this._entries));
    } catch (e) {
      // Storage full or unavailable - silently degrade to memory-only
    }
  };

  /**
   * Destroy the history instance, stop monitoring and clean up
   */
  ClipboardHistory.prototype.destroy = function () {
    if (this._destroyed) return;
    this._destroyed = true;
    this.stop();
    this._entries = [];
  };

  // Expose ClipboardHistory as static property
  NowClipboard.History = ClipboardHistory;

  // ========================================
  // 11. Cross-Tab Sync (BroadcastChannel)
  // ========================================

  /**
   * Start cross-tab synchronization via BroadcastChannel
   * When clipboard operations (copy/cut) happen in one tab, other tabs are notified
   * @param {Object} [options] - Configuration
   * @param {string} [options.channel='nowclipboard'] - BroadcastChannel name
   * @param {boolean} [options.autoSync=true] - Automatically sync copy/cut operations
   * @returns {{ destroy: Function, broadcast: Function }}
   */
  NowClipboard.onSync = function (options) {
    if (!_isBrowser) {
      return {
        destroy: function () {},
        broadcast: function () {}
      };
    }

    // Check BroadcastChannel support
    if (typeof BroadcastChannel === 'undefined') {
      // Silently return stub if not supported
      return {
        destroy: function () {},
        broadcast: function () {}
      };
    }

    var opts = options || {};
    var channelName = opts.channel || 'nowclipboard';
    var autoSync = opts.autoSync !== false; // default true
    var bc = new BroadcastChannel(channelName);
    var listeners = [];
    var destroyed = false;

    // Listen for broadcasts from other tabs
    bc.onmessage = function (event) {
      var data = event.data;
      if (!data || !data.type) return;

      for (var i = 0; i < listeners.length; i++) {
        try {
          listeners[i](data);
        } catch (e) {
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('NowClipboard onSync callback error:', e);
          }
        }
      }
    };

    // Auto-sync: intercept copy/cut operations and broadcast them
    var originalCopy = NowClipboard.copy;
    var originalCut = NowClipboard.cut;

    if (autoSync) {
      NowClipboard.copy = function (text, opts) {
        return originalCopy.call(NowClipboard, text, opts).then(function (result) {
          if (!destroyed) {
            try {
              bc.postMessage({ type: 'copy', text: result, timestamp: Date.now() });
            } catch (e) { /* ignore */ }
          }
          return result;
        });
      };

      NowClipboard.cut = function (element, opts) {
        return originalCut.call(NowClipboard, element, opts).then(function (result) {
          if (!destroyed) {
            try {
              bc.postMessage({ type: 'cut', text: result, timestamp: Date.now() });
            } catch (e) { /* ignore */ }
            }
          return result;
        });
      };
    }

    return {
      /**
       * Broadcast a custom message to other tabs
       * @param {Object} data - Data to broadcast
       */
      broadcast: function (data) {
        if (!destroyed) {
          try {
            bc.postMessage(data);
          } catch (e) { /* ignore */ }
        }
      },

      /**
       * Add a listener for sync events
       * @param {Function} callback - Callback function
       */
      addListener: function (callback) {
        if (_isFunction(callback)) {
          listeners.push(callback);
        }
      },

      /**
       * Remove a listener
       * @param {Function} callback - Callback function to remove
       */
      removeListener: function (callback) {
        var idx = listeners.indexOf(callback);
        if (idx !== -1) {
          listeners.splice(idx, 1);
        }
      },

      /**
       * Destroy the sync instance and restore original methods
       */
      destroy: function () {
        if (destroyed) return;
        destroyed = true;

        // Restore original copy/cut methods
        if (autoSync) {
          NowClipboard.copy = originalCopy;
          NowClipboard.cut = originalCut;
        }

        bc.close();
        listeners = [];
      }
    };
  };

  return NowClipboard;
});
